import React from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Button,
  InputGroup,
  Form, FormGroup, Label
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminSongs extends React.Component {
  state = {
    isLoading: false,
    songs: [],
    totalPages: 0,
    currentPage: 0,
    searchQuery: "",
    showUnThemedOnly: false,
    showUnReferencedOnly: false,
    showNoSongBooksOnly: false,
    unauthorized: false,
  };

  componentDidMount() {
    try {
      const customer = sessionStorage.getItem("customer");
      const parsed = customer ? JSON.parse(customer) : null;
      console.log("AdminSongs customer structure:", parsed);
      if (parsed?.user?.isAdmin) {
        this.loadPage(0);
      } else {
        this.setState({ unauthorized: true });
      }
    } catch (e) {
      console.error("AdminSongs componentDidMount error:", e);
      this.setState({ unauthorized: true });
    }
  }

  /**
   * loadPage:
   * Calls the paginated API, including optional searchQuery from state.
   * pageNumber is 0-based.
   */
  loadPage = (pageNumber) => {
    const { searchQuery, showUnThemedOnly, showUnReferencedOnly, showNoSongBooksOnly } = this.state;

    const params = new URLSearchParams({
      page:  pageNumber,
      size:  10
    });

    if (searchQuery?.trim()) {
      params.append("query", searchQuery.trim());
    }
    if (showUnThemedOnly) {
      params.append("noThemes", "true");
    }
    if (showUnReferencedOnly) {
      params.append("noReferences", "true");
    }
    if (showNoSongBooksOnly) {
      params.append("noSongBooks", "true");
    }

    this.setState({ isLoading: true });
    Api.get(`/words/paginated?${params.toString()}`)
        .then(response => {
          this.setState({
            songs:       response.data.content,
            totalPages:  response.data.totalPages,
            currentPage: pageNumber,
            isLoading:   false
          });
        })
        .catch(err => {
          console.error("Error fetching page:", err);
          this.setState({ isLoading: false });
        });
  };

  handleSearchChange = (e) => {
    this.setState({ searchQuery: e.target.value });
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.loadPage(0);
  };

  handleClearSearch = () => {
    this.setState({ searchQuery: "" }, () => {
      this.loadPage(0);
    });
  };

  renderPagination = () => {
    const { currentPage, totalPages } = this.state;
    if (totalPages <= 1) {
      return null;
    }

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;
    if (endPage > totalPages - 1) {
      endPage = totalPages - 1;
      startPage = Math.max(0, endPage - (maxVisiblePages - 1));
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
          <PaginationItem active={i === currentPage} key={i}>
            <PaginationLink onClick={() => this.loadPage(i)}>
              {i + 1}
            </PaginationLink>
          </PaginationItem>
      );
    }

    return (
        <Pagination aria-label="Songs pagination" className="justify-content-center mt-4 flex-wrap">
          {/* Previous */}
          <PaginationItem disabled={currentPage === 0}>
            <PaginationLink previous onClick={() => this.loadPage(currentPage - 1)} />
          </PaginationItem>

          {/* Leading first page + ellipsis */}
          {startPage > 0 && (
              <>
                <PaginationItem>
                  <PaginationLink onClick={() => this.loadPage(0)}>1</PaginationLink>
                </PaginationItem>
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              </>
          )}

          {/* Page window */}
          {pages}

          {/* Trailing ellipsis + last page */}
          {endPage < totalPages - 1 && (
              <>
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink onClick={() => this.loadPage(totalPages - 1)}>
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
          )}

          {/* Next */}
          <PaginationItem disabled={currentPage === totalPages - 1}>
            <PaginationLink next onClick={() => this.loadPage(currentPage + 1)} />
          </PaginationItem>
        </Pagination>
    );
  };

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }
    if (this.state.unauthorized) return <Navigate to="/" />;
    const { songs, isLoading, searchQuery } = this.state;
    const { showUnThemedOnly } = this.state;

    return (
        <>
          <SeoNoIndex noindex={true} />

          <div className="position-relative">
            {/* Hero Section */}
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span /><span /><span /><span /><span /><span /><span /><span /><span />
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows<br/><span>manage songs</span>
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    viewBox="0 0 2560 100"
                >
                  <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                </svg>
              </div>
            </section>

            {/* Main Admin Panel */}
            <section className="section section-lg pt-lg-0 mt--200">
              <Container>
                <Row className="justify-content-center">
                  <Col lg="12">
                    <Row className="row-grid">
                      <Col lg="12">
                        <Card className="shadow border-0">
                          <Row className="row-grid">
                            <CardBody className="py-5">
                              {/* Back to menu */}
                              <Button color="link" onClick={() => this.props.navigate("/admin")} className="mb-3 p-0">
                                &larr; Back to menu
                              </Button>

                              {/* Search Form with Clear button */}
                              <Form inline onSubmit={this.handleSearchSubmit} className="mb-3">
                                <InputGroup>
                                  <Input
                                      type="text"
                                      placeholder="Search by title or song ID..."
                                      value={this.state.searchQuery}
                                      onChange={this.handleSearchChange}
                                  />
                                  <Button color="primary" type="submit">Search</Button>
                                  <Button
                                      color="secondary"
                                      type="button"
                                      onClick={this.handleClearSearch}
                                      disabled={!this.state.searchQuery.trim()}
                                      className="ml-2"
                                  >
                                    Clear
                                  </Button>
                                </InputGroup>

                                {/* filter: no themes */}
                                <FormGroup check inline className="ml-3">
                                  <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={this.state.showUnThemedOnly}
                                        onChange={e => this.setState(
                                            { showUnThemedOnly: e.target.checked },
                                            () => this.loadPage(0)
                                        )}
                                    />{' '}
                                    No Themes
                                  </Label>
                                </FormGroup>

                                {/* filter: no references */}
                                <FormGroup check inline className="ml-2">
                                  <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={this.state.showUnReferencedOnly}
                                        onChange={e => this.setState(
                                            { showUnReferencedOnly: e.target.checked },
                                            () => this.loadPage(0)
                                        )}
                                    />{' '}
                                    No References
                                  </Label>
                                </FormGroup>

                                {/* filter: no song books */}
                                <FormGroup check inline className="ml-2">
                                  <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={this.state.showNoSongBooksOnly}
                                        onChange={e => this.setState(
                                            { showNoSongBooksOnly: e.target.checked },
                                            () => this.loadPage(0)
                                        )}
                                    />{' '}
                                    No Song Books
                                  </Label>
                                </FormGroup>
                              </Form>


                              {/* Song List */}
                              {isLoading ? (
                                  <div>Loading...</div>
                              ) : (
                                  <ul className="list-group">
                                    {songs.length > 0 ? (
                                        songs.map((song) => (
                                            <li
                                                key={song.id}
                                                className="list-group-item"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => this.props.navigate(`/admin/song/${song.id}`)}
                                            >
                                                <strong>{song.title}</strong>
                                                {song.alternativeTitle && <> <em>({song.alternativeTitle})</em></>}
                                                {song.authors?.length > 0 && (
                                                    <div className="small text-muted">
                                                        {song.authors.map(a => a.name).join(', ')}
                                                    </div>
                                                )}
                                                <div className="small text-muted">
                                                    ({song.id})
                                                </div>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="list-group-item">No songs found.</li>
                                    )}
                                  </ul>
                              )}

                              {/* Pagination */}
                              {this.renderPagination()}
                            </CardBody>
                          </Row>
                        </Card>
                      </Col>
                    </Row>
                    <Row>&nbsp;</Row>
                  </Col>
                </Row>
              </Container>
            </section>
          </div>
        </>
    );
  }
}

// Wrapper to inject navigate and params into class component
function withRouterParamsAndNavigate(Component) {
  return function Wrapped(props) {
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} navigate={navigate} params={params} />;
  };
}

export default withRouterParamsAndNavigate(AdminSongs);
